import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, Bell, Shield, Globe, Eye, Lock, Mail,
  Trash2, ChevronRight, AlertTriangle, Check, Download, Phone, MessageSquare,
} from "lucide-react";

import { toast } from "sonner";

export default function SettingsPage() {
  const { api, user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [notifsEnabled, setNotifsEnabled] = useState(true);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [pushTestMsg, setPushTestMsg] = useState("");
  const [privateProfile, setPrivateProfile] = useState(false);
  const [emailPrefs, setEmailPrefs] = useState({ emails_enabled: true, recap_frequency: "daily" });
  const [emailPrefsLoading, setEmailPrefsLoading] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: "", new: "", confirm: "" });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordMsg, setPasswordMsg] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  // SMS states
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [smsSending, setSmsSending] = useState(false);
  const [smsStep, setSmsStep] = useState("idle"); // idle | sent | verified
  const [smsMsg, setSmsMsg] = useState("");
  const [smsPrefs, setSmsPrefs] = useState({
    ticket_confirmation: true, auction_alerts: true,
    event_reminders: true, payout_notifications: true,
  });

  useEffect(() => {
    api.get("/admin/emails/preferences").then((res) => setEmailPrefs(res.data)).catch(() => {});
    api.get("/push/status").then((res) => setPushEnabled(res.data.subscribed)).catch(() => {});
    api.get("/sms/preferences").then((res) => {
      if (res.data.phone) setPhoneNumber(res.data.phone);
      if (res.data.phone_verified) { setPhoneVerified(true); setSmsStep("verified"); }
      if (res.data.preferences) setSmsPrefs(res.data.preferences);
    }).catch(() => {});
  }, [api]);

  const handleSaveEmailPrefs = async (newPrefs) => {
    setEmailPrefsLoading(true);
    try {
      const res = await api.put("/admin/emails/preferences", newPrefs);
      setEmailPrefs(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
    setEmailPrefsLoading(false);
  };

  const handleExportData = async () => {
    setExporting(true);
    try {
      const res = await api.get("/auth/export-data");
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `reallyfan-data-${user.pseudo}-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'export");
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm("ATTENTION : Cette action est IRREVERSIBLE. Toutes vos donnees seront supprimees. Confirmez-vous ?")) return;
    setDeleteLoading(true);
    try {
      await api.delete("/auth/account");
      logout();
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la suppression");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    if (passwordForm.new !== passwordForm.confirm) {
      setPasswordMsg("Les mots de passe ne correspondent pas");
      return;
    }
    if (passwordForm.new.length < 6) {
      setPasswordMsg("Le mot de passe doit contenir au moins 6 caracteres");
      return;
    }
    setPasswordLoading(true);
    try {
      await api.put("/auth/password", {
        current_password: passwordForm.current,
        new_password: passwordForm.new,
      });
      setPasswordMsg("Mot de passe modifie avec succes !");
      setPasswordForm({ current: "", new: "", confirm: "" });
      setTimeout(() => {
        setChangingPassword(false);
        setPasswordMsg("");
      }, 2000);
    } catch (err) {
      setPasswordMsg(err.response?.data?.detail || "Erreur lors du changement");
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleSendOTP = async () => {
    if (!phoneNumber || phoneNumber.length < 10) return setSmsMsg("Numero invalide");
    setSmsSending(true); setSmsMsg("");
    try {
      const res = await api.post("/sms/verify/send", { phone: phoneNumber });
      setSmsStep("sent");
      setSmsMsg(res.data.message);
    } catch (err) {
      setSmsMsg(err.response?.data?.detail || "Erreur envoi SMS");
    }
    setSmsSending(false);
  };

  const handleConfirmOTP = async () => {
    if (!otpCode || otpCode.length < 4) return setSmsMsg("Code invalide");
    setSmsSending(true); setSmsMsg("");
    try {
      await api.post("/sms/verify/confirm", { phone: phoneNumber, code: otpCode });
      setSmsStep("verified"); setPhoneVerified(true);
      setSmsMsg("Numero verifie !");
      refreshUser();
    } catch (err) {
      setSmsMsg(err.response?.data?.detail || "Code invalide");
    }
    setSmsSending(false);
  };

  const handleTestPush = async () => {
    setPushTestMsg("Envoi en cours...");
    try {
      const res = await api.post("/push/test");
      setPushTestMsg(res.data.success ? "Push envoyee, regardez vos notifications !" : "Aucun appareil abonne.");
    } catch {
      setPushTestMsg("Erreur lors du test push");
    }
    setTimeout(() => setPushTestMsg(""), 4000);
  };

  const handleSaveSmsPrefs = async (key, value) => {
    const newPrefs = { ...smsPrefs, [key]: value };
    setSmsPrefs(newPrefs);
    try { await api.put("/sms/preferences", newPrefs); } catch (err) { console.error(err); }
  };

  const sections = [
    {
      title: "Compte",
      items: [
        {
          icon: Mail,
          label: "Email",
          value: user?.email || "",
          action: null,
        },
        {
          icon: Lock,
          label: "Changer le mot de passe",
          action: () => setChangingPassword(!changingPassword),
          chevron: true,
        },
      ],
    },
    {
      title: "Confidentialite",
      items: [
        {
          icon: Eye,
          label: "Profil prive",
          description: "Seuls tes abonnes verront ton profil",
          toggle: true,
          value: privateProfile,
          onChange: () => setPrivateProfile(!privateProfile),
        },
      ],
    },
    {
      title: "Notifications",
      items: [
        {
          icon: Bell,
          label: "Notifications in-app",
          description: "Recevoir les notifications d'activite",
          toggle: true,
          value: notifsEnabled,
          onChange: () => setNotifsEnabled(!notifsEnabled),
        },
        {
          icon: Bell,
          label: "Notifications push",
          description: pushEnabled ? "Actives sur cet appareil" : "Recevoir des alertes en temps reel",
          toggle: true,
          value: pushEnabled,
          onChange: async () => {
            setPushLoading(true);
            try {
              if (pushEnabled) {
                await api.delete("/push/unsubscribe");
                setPushEnabled(false);
              } else {
                const { requestNotificationPermission } = await import("@/lib/firebase");
                const token = await requestNotificationPermission();
                if (token) {
                  await api.post("/push/subscribe", { endpoint: token, keys: { fcm: true } });
                  setPushEnabled(true);
                } else {
                  toast.success("Permission refusee. Active les notifications dans les parametres de ton navigateur.");
                }
              }
            } catch {
              toast.error("Erreur lors de la configuration des notifications push");
            }
            setPushLoading(false);
          },
        },
        {
          icon: Mail,
          label: "Emails",
          description: emailPrefs.emails_enabled ? "Actives" : "Desactives",
          toggle: true,
          value: emailPrefs.emails_enabled,
          onChange: () => handleSaveEmailPrefs({ ...emailPrefs, emails_enabled: !emailPrefs.emails_enabled }),
        },
      ],
    },
    {
      title: "Frequence du recap email",
      description: "Choisis a quelle frequence recevoir ton resume d'activite",
      items: [
        { label: "Recap quotidien", description: "Chaque matin, les nouvelles encheres et l'activite du jour", radio: true, value: emailPrefs.recap_frequency === "daily", onChange: () => handleSaveEmailPrefs({ ...emailPrefs, recap_frequency: "daily" }) },
        { label: "Recap hebdomadaire", description: "Un resume chaque lundi matin", radio: true, value: emailPrefs.recap_frequency === "weekly", onChange: () => handleSaveEmailPrefs({ ...emailPrefs, recap_frequency: "weekly" }) },
        { label: "Alertes importantes uniquement", description: "Encheres gagnees/perdues, surencheres", radio: true, value: emailPrefs.recap_frequency === "important_only", onChange: () => handleSaveEmailPrefs({ ...emailPrefs, recap_frequency: "important_only" }) },
        { label: "Aucun email", description: "Desactiver tous les emails (sauf securite)", radio: true, value: emailPrefs.recap_frequency === "none", onChange: () => handleSaveEmailPrefs({ ...emailPrefs, recap_frequency: "none" }) },
      ],
    },
    {
      title: "A propos",
      items: [
        {
          icon: Shield,
          label: "Politique de confidentialite",
          action: () => navigate("/privacy"),
          chevron: true,
        },
        {
          icon: Shield,
          label: "Conditions d'utilisation",
          action: () => navigate("/legal"),
          chevron: true,
        },
        {
          icon: Globe,
          label: "Aide & FAQ",
          action: () => navigate("/help"),
          chevron: true,
        },
      ],
    },
    {
      title: "Mes donnees (RGPD)",
      items: [
        {
          icon: Download,
          label: "Telecharger mes donnees",
          description: "Exporter toutes vos donnees au format JSON",
          action: handleExportData,
          chevron: true,
        },
      ],
    },
  ];

  return (
    <div className="py-6 space-y-5" data-testid="settings-page">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-xl text-muted-foreground hover:text-[#AFA8BA] transition-colors"
          data-testid="settings-back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <h1 className="font-heading font-extrabold text-xl text-foreground">Parametres</h1>
      </div>

      {sections.map((section) => (
        <motion.div
          key={section.title}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-card border border-border rounded-3xl overflow-hidden shadow-sm"
        >
          <p className="px-5 pt-4 pb-2 text-xs font-bold text-muted-foreground uppercase tracking-wider">{section.title}</p>
          <div className="divide-y divide-[#E8E0D8]">
            {section.items.map((item, i) => (
              <div key={i}>
                {item.radio ? (
                  <button
                    onClick={item.onChange}
                    className="w-full flex items-center justify-between px-5 py-3 hover:bg-muted transition-colors"
                    data-testid={`radio-${item.label.replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    <div className="text-left">
                      <p className="text-sm text-card-foreground">{item.label}</p>
                      {item.description && <p className="text-[10px] text-muted-foreground">{item.description}</p>}
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                      item.value ? "border-[#FF6B35] bg-[#FF6B35]" : "border-border"
                    }`}>
                      {item.value && <div className="w-2 h-2 rounded-full bg-white" />}
                    </div>
                  </button>
                ) : item.toggle ? (
                  <div className="flex items-center justify-between px-5 py-3.5">
                    <div className="flex items-center gap-3">
                      <item.icon className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-card-foreground">{item.label}</p>
                        {item.description && <p className="text-[10px] text-muted-foreground">{item.description}</p>}
                      </div>
                    </div>
                    <button
                      onClick={item.onChange}
                      className={`w-11 h-6 rounded-full transition-colors relative ${
                        item.value ? "bg-[#FF6B35]" : "bg-border"
                      }`}
                      data-testid={`toggle-${item.label.replace(/\s+/g, "-").toLowerCase()}`}
                    >
                      <div
                        className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
                          item.value ? "translate-x-5.5 left-[22px]" : "left-0.5"
                        }`}
                      />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={item.action}
                    disabled={!item.action}
                    className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-muted transition-colors disabled:cursor-default"
                  >
                    <div className="flex items-center gap-3">
                      {item.icon && <item.icon className="w-4 h-4 text-muted-foreground" />}
                      <div className="text-left">
                        <p className="text-sm text-card-foreground">{item.label}</p>
                        {item.value && <p className="text-[10px] text-muted-foreground">{item.value}</p>}
                      </div>
                    </div>
                    {item.chevron && <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                  </button>
                )}
              </div>
            ))}
          </div>
        </motion.div>
      ))}

      {/* Push test button */}
      {pushEnabled && (
        <div className="bg-card border border-border rounded-3xl p-4 shadow-sm flex items-center justify-between" data-testid="push-test-block">
          <div className="flex items-center gap-3">
            <Bell className="w-4 h-4 text-[#FF6B35]" />
            <div>
              <p className="text-sm font-medium text-card-foreground">Tester les notifications push</p>
              {pushTestMsg && <p className="text-[10px] text-muted-foreground">{pushTestMsg}</p>}
            </div>
          </div>
          <button
            onClick={handleTestPush}
            disabled={pushLoading}
            className="bg-[#FF6B35] text-white font-bold px-4 py-2 rounded-full text-xs hover:scale-105 active:scale-95 transition-transform disabled:opacity-50"
            data-testid="test-push-btn"
          >
            Envoyer
          </button>
        </div>
      )}

      {/* Password Change */}
      {changingPassword && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-card border border-border rounded-3xl p-5 space-y-3 shadow-sm"
          data-testid="password-change-form"
        >
          <h3 className="font-heading font-bold text-sm text-card-foreground">Changer le mot de passe</h3>
          <input
            type="password"
            placeholder="Mot de passe actuel"
            value={passwordForm.current}
            onChange={(e) => setPasswordForm({ ...passwordForm, current: e.target.value })}
            className="w-full bg-white border border-border rounded-xl px-3 py-2.5 text-sm text-card-foreground placeholder:text-muted-foreground focus:border-[#FF6B35] outline-none"
            data-testid="current-password-input"
          />
          <input
            type="password"
            placeholder="Nouveau mot de passe"
            value={passwordForm.new}
            onChange={(e) => setPasswordForm({ ...passwordForm, new: e.target.value })}
            className="w-full bg-white border border-border rounded-xl px-3 py-2.5 text-sm text-card-foreground placeholder:text-muted-foreground focus:border-[#FF6B35] outline-none"
            data-testid="new-password-input"
          />
          <input
            type="password"
            placeholder="Confirmer le nouveau mot de passe"
            value={passwordForm.confirm}
            onChange={(e) => setPasswordForm({ ...passwordForm, confirm: e.target.value })}
            className="w-full bg-white border border-border rounded-xl px-3 py-2.5 text-sm text-card-foreground placeholder:text-muted-foreground focus:border-[#FF6B35] outline-none"
            data-testid="confirm-password-input"
          />
          {passwordMsg && (
            <p className={`text-xs ${passwordMsg.includes("succes") ? "text-emerald-500" : "text-red-500"}`}>
              {passwordMsg}
            </p>
          )}
          <button
            onClick={handlePasswordChange}
            disabled={passwordLoading || !passwordForm.current || !passwordForm.new || !passwordForm.confirm}
            className="bg-[#FF6B35] text-white font-bold px-5 py-2.5 rounded-full text-xs hover:scale-105 active:scale-95 transition-transform disabled:opacity-50 flex items-center gap-1"
            data-testid="submit-password-change"
          >
            <Check className="w-3 h-3" />
            {passwordLoading ? "..." : "Changer"}
          </button>
        </motion.div>
      )}

      {/* Delete Account */}

      {/* SMS Notifications */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-card border border-border rounded-3xl p-5 space-y-4 shadow-sm"
        data-testid="sms-section"
      >
        <div className="flex items-center gap-2">
          <Phone className="w-4 h-4 text-[#FF6B35]" />
          <h3 className="font-heading font-bold text-sm text-card-foreground">Notifications SMS</h3>
        </div>

        {/* Phone verification */}
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">
            {phoneVerified ? "Numero verifie" : "Verifiez votre numero pour recevoir des SMS"}
          </p>
          <div className="flex gap-2">
            <input
              type="tel"
              placeholder="06 12 34 56 78"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              disabled={phoneVerified}
              className="flex-1 bg-white border border-border rounded-xl px-3 py-2.5 text-sm text-card-foreground placeholder:text-muted-foreground focus:border-[#FF6B35] outline-none disabled:opacity-60"
              data-testid="phone-input"
            />
            {!phoneVerified && smsStep === "idle" && (
              <button
                onClick={handleSendOTP}
                disabled={smsSending}
                className="bg-[#FF6B35] text-white font-bold px-4 py-2.5 rounded-xl text-xs hover:scale-105 active:scale-95 transition-transform disabled:opacity-50"
                data-testid="send-otp-btn"
              >
                {smsSending ? "..." : "Verifier"}
              </button>
            )}
            {phoneVerified && (
              <div className="flex items-center gap-1 px-3 py-2 bg-emerald-50 rounded-xl">
                <Check className="w-4 h-4 text-emerald-500" />
                <span className="text-xs text-emerald-600 font-medium">Verifie</span>
              </div>
            )}
          </div>
          {smsStep === "sent" && !phoneVerified && (
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Code a 6 chiffres"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value)}
                maxLength={6}
                className="flex-1 bg-white border border-border rounded-xl px-3 py-2.5 text-sm text-card-foreground text-center tracking-widest font-mono-credits placeholder:text-muted-foreground focus:border-[#FF6B35] outline-none"
                data-testid="otp-input"
              />
              <button
                onClick={handleConfirmOTP}
                disabled={smsSending}
                className="bg-emerald-500 text-white font-bold px-4 py-2.5 rounded-xl text-xs hover:scale-105 active:scale-95 transition-transform disabled:opacity-50"
                data-testid="confirm-otp-btn"
              >
                {smsSending ? "..." : "Confirmer"}
              </button>
            </div>
          )}
          {smsMsg && (
            <p className={`text-xs ${smsMsg.includes("verifie") || smsMsg.includes("envoye") ? "text-emerald-500" : "text-red-500"}`}>
              {smsMsg}
            </p>
          )}
        </div>

        {/* SMS Preferences - only shown if phone is verified */}
        {phoneVerified && (
          <div className="space-y-2 pt-2 border-t border-border">
            <p className="text-xs text-muted-foreground font-medium">Recevoir un SMS pour :</p>
            {[
              { key: "ticket_confirmation", label: "Confirmation d'achat de billets" },
              { key: "auction_alerts", label: "Alertes encheres (surenchere)" },
              { key: "event_reminders", label: "Rappels d'evenements (J-1)" },
              { key: "payout_notifications", label: "Notifications de versement" },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => handleSaveSmsPrefs(key, !smsPrefs[key])}
                className="w-full flex items-center justify-between py-2 px-1"
                data-testid={`sms-pref-${key}`}
              >
                <span className="text-xs text-card-foreground">{label}</span>
                <div className={`w-9 h-5 rounded-full flex items-center transition-colors ${smsPrefs[key] ? "bg-[#FF6B35] justify-end" : "bg-border justify-start"}`}>
                  <div className="w-4 h-4 rounded-full bg-white shadow-sm mx-0.5" />
                </div>
              </button>
            ))}
          </div>
        )}
      </motion.div>

      {/* Delete Account */}
      <div className="bg-card border border-red-100 rounded-3xl overflow-hidden shadow-sm">
        <button
          onClick={() => setShowDeleteConfirm(!showDeleteConfirm)}
          className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-red-50/50 transition-colors"
          data-testid="delete-account-btn"
        >
          <div className="flex items-center gap-3">
            <Trash2 className="w-4 h-4 text-red-400" />
            <p className="text-sm text-red-500">Supprimer mon compte</p>
          </div>
          <ChevronRight className="w-4 h-4 text-red-300" />
        </button>
        {showDeleteConfirm && (
          <div className="px-5 pb-4 pt-2 border-t border-red-100 space-y-3">
            <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 border border-red-100">
              <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
              <p className="text-xs text-red-600">
                Cette action est irreversible. Toutes vos donnees seront definitivement supprimees
                (profil, publications, commentaires, mises, credits, badges).
              </p>
            </div>
            <button
              onClick={handleDeleteAccount}
              disabled={deleteLoading}
              className="w-full bg-red-500 text-white font-bold py-2.5 rounded-xl text-xs hover:bg-red-600 transition-colors disabled:opacity-50"
              data-testid="confirm-delete-btn"
            >
              {deleteLoading ? "Suppression en cours..." : "Confirmer la suppression definitive"}
            </button>
          </div>
        )}
      </div>

      {/* Version */}
      <p className="text-center text-[10px] text-muted-foreground">ReallyFan v1.0.0</p>
    </div>
  );
}
